import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  padding: 2rem;
`;

const Card = styled(motion.div)`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 1px solid rgba(255, 107, 53, 0.3);
  border-radius: 16px;
  padding: 3rem;
  max-width: 500px;
  width: 100%;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(255, 107, 53, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff6b35 0%, #ff8c5a 50%, #ff6b35 100%);
    background-size: 200% 100%;
    animation: shimmer 2s linear infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const IconContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, rgba(255, 107, 53, 0.2) 0%, rgba(255, 140, 90, 0.2) 100%);
  border-radius: 50%;
  margin: 0 auto 2rem;
  color: #ff6b35;
  position: relative;
`;

const SuccessIcon = styled(CheckCircle)`
  width: 60px;
  height: 60px;
  stroke-width: 2;
`;

const SparkleIcon = styled(Sparkles)`
  position: absolute;
  width: 24px;
  height: 24px;
  color: #ff8c5a;
  opacity: 0.8;
  
  &:nth-child(1) {
    top: 10px;
    right: 10px;
    animation: sparkle 2s ease-in-out infinite;
  }
  
  &:nth-child(2) {
    bottom: 10px;
    left: 10px;
    animation: sparkle 2s ease-in-out infinite 0.5s;
  }
  
  &:nth-child(3) {
    top: 50%;
    right: -10px;
    animation: sparkle 2s ease-in-out infinite 1s;
  }

  @keyframes sparkle {
    0%, 100% {
      opacity: 0.3;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1.2);
    }
  }
`;

const Title = styled(motion.h1)`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #ffffff 0%, #ff6b35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
`;

const Subtitle = styled(motion.p)`
  color: #cccccc;
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const Message = styled(motion.div)`
  background: rgba(34, 197, 94, 0.1);
  border: 1px solid rgba(34, 197, 94, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  color: #22c55e;
  font-size: 1rem;
  line-height: 1.6;
`;

const Button = styled(motion.button)`
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c5a 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  padding: 1rem 2.5rem;
  font-weight: 600;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 1.5rem auto 0;
  min-width: 250px;
  box-shadow: 0 4px 15px rgba(255, 107, 53, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 107, 53, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    width: 100%;
    min-width: auto;
  }
`;

const EmailVerificationSuccess = ({ onContinue }) => {
  const navigate = useNavigate();

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
    } else {
      // Navigate to home or dashboard
      navigate('/');
    }
  };

  return (
    <Container>
      <Card
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, type: 'spring' }}
      >
        <IconContainer
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
        >
          <SuccessIcon />
          <SparkleIcon />
          <SparkleIcon />
          <SparkleIcon />
        </IconContainer>
        
        <Title
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          Email Verified!
        </Title>
        
        <Subtitle
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          Thank you for verifying your email address.
        </Subtitle>
        
        <Message
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Your account has been successfully verified. You can now log in and access your account.
        </Message>
        
        <Button
          onClick={handleContinue}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Continue to Site
          <ArrowRight size={20} />
        </Button>
      </Card>
    </Container>
  );
};

export default EmailVerificationSuccess;

